<template>
  <BaseEditor
    data-test-id="editDataset"
    v-bind="$attrs"
    v-model="store.showEditDialog"
    :table-store-id="TableStoreId.Datasets"
    :title="title"
    :max-width="400"
    :min-height="400"
    @save-item="saveItem"
  >
    <v-form :fast-fail="false" @submit.prevent ref="editDatasetForm">
      <v-row>
        <v-col cols="12" xs="12" sm="12" md="12" lg="12" xl="12" xxl="12">
          <PrimaryTextField
            required
            :label="t('fields.datasets.name')"
            :maxlength="64"
            v-model.trim="item.name"
            :rules="RulesManager.getRules(Rule.Required)"
          />
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="12" lg="12" xl="12" xxl="12">
          <PrimarySelect
            required
            :items="timeZoneValues"
            :label="t('fields.datasets.timeZone')"
            :maxlength="64"
            v-model.trim="item.timeZone"
            :rules="RulesManager.getRules(Rule.Required)"
          />
        </v-col>
        <v-col cols="6" xs="6" sm="6" md="6" lg="6" xl="6" xxl="6">
          <PrimarySwitch
            v-if="!isDefined(item.id)"
            :label="t('fields.datasets.isSandbox')"
            v-model="item.isSandbox"
          />
          <DisplayText
            v-if="isDefined(item.id)"
            :label="t('fields.datasets.type')"
            :value="datasetType"
          />
        </v-col>
        <v-col cols="6" xs="6" sm="6" md="6" lg="6" xl="6" xxl="6">
          <PrimarySwitch
            :label="t('fields.datasets.isEnabled')"
            v-model="item.isEnabled"
            :disabled="isActive"
          />
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="12" lg="12" xl="12" xxl="12">
          <PrimaryTextArea
            :rows="5"
            :maxlength="250"
            v-model="item.description"
            :label="t('fields.datasets.description')"
          />
        </v-col>
      </v-row>
    </v-form>
  </BaseEditor>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { useRulesManager } from '@/composables/RulesManager'
import { isDefined } from '@/composables/utils'
import { Rule } from '@/enums/Rule'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IDatasetStore } from '@/interfaces/api/IDatasetStore'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import { DatasetRecord } from '@/models/DatasetRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DisplayText from '../DisplayText.vue'
import PrimarySwitch from '../inputs/PrimarySwitch.vue'

const store = TableStoreFactory.get(TableStoreId.Datasets) as IDatasetStore
const RulesManager = useRulesManager()
const settingsStore = useSettingsStore()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES
const editDatasetForm = ref<any>()

// COMPUTED PROPERTIES
const datasetType = computed(() => {
  return item.value.isSandbox ? t('labels.sandbox') : t('labels.production')
})

const item = computed(() => {
  if (!store.selectedItem) {
    return new DatasetRecord()
  }
  return store.selectedItem as DatasetRecord
})

const isActive = computed(() => {
  return item.value.id === settingsStore.activeDatasetId
})

const timeZoneValues = computed(() => {
  const timeZoneValues = Intl.supportedValuesOf('timeZone')
  const timeZones: Array<ISimpleOption> = []
  for (let index = 0; index < 416; index++) {
    const title: string = t(`timeZones.${index.toString()}`).replace('_', ' ')
    timeZones.push({ title, value: timeZoneValues[index] })
  }
  return timeZones
})

const title = computed((): string => {
  if (item.value?.id) {
    return t('dialogs.headers.editDataset')
  }
  return t('dialogs.headers.createDataset')
})

// WATCHERS

// LIFECYCLE HOOKS

// FUNCTIONS
const saveItem = async () => {
  const { valid } = await editDatasetForm.value.validate()
  if (!valid) {
    return
  }

  await store.save()
  await store.loadEnabled()
}
</script>
